import { useCallback, useMemo, useRef, useState } from 'react';
import {
  createClientScope,
  getActiveClientScope,
  setActiveClientScope,
} from '../services/clientStateService';
import { confirmLeave, setActiveLeaveGuardTab } from '../utils/leaveGuards';

/**
 * Workspace tabs: several pages/clients open at once in the header. Each tab
 * keeps its own page, section and client scope, and its page stays mounted
 * (hidden) while another tab is in front, so unsaved input, filters and scroll
 * position survive a switch. A tab's page mounts the first time it is shown.
 */
export const MAX_WORKSPACE_TABS = 6;
export const WORKSPACE_TABS_STORAGE_KEY = 'advicelink.workspaceTabs';

let nextTabSeq = 1;
const newTabId = () => `wt-${Date.now().toString(36)}-${nextTabSeq++}`;

function makeTab(page, section = null) {
  return {
    id: newTabId(),
    page,
    section,
    scrollToSectionId: null,
    portalSearchPrefill: null,
    documentStorageBreadcrumbPath: [],
    settingsMenuRequest: 0,
    mounted: false,
  };
}

/** Saved tab locations (`{ tabs: [{ page, section, clientId }], activeIndex, viewAs }`) or null. */
export function readPersistedWorkspaceTabs() {
  try {
    const raw = sessionStorage.getItem(WORKSPACE_TABS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.tabs) || parsed.tabs.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPersistedWorkspaceTabs() {
  try {
    sessionStorage.removeItem(WORKSPACE_TABS_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

function persist(tabs, activeId, scopes, viewAs) {
  try {
    const data = {
      tabs: tabs.map((t) => ({
        page: t.page,
        section: t.section ?? null,
        clientId: scopes.get(t.id)?.clientId ?? null,
      })),
      activeIndex: Math.max(0, tabs.findIndex((t) => t.id === activeId)),
      viewAs: viewAs ?? null,
    };
    sessionStorage.setItem(WORKSPACE_TABS_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable — tabs just won't come back after a reload.
  }
}

/**
 * @param {object} opts
 * @param {string} opts.initialPage - page for the first tab
 * @param {() => void} [opts.onLimitReached] - called when opening a tab past MAX_WORKSPACE_TABS
 */
export function useWorkspaceTabs({ initialPage, onLimitReached } = {}) {
  // Scopes are mutable service objects: kept beside the tab state, keyed by tab id.
  const scopesRef = useRef(null);
  const [state, setState] = useState(() => {
    // Fixed id: a StrictMode double-invoke of this initializer must agree with itself.
    const first = { ...makeTab(initialPage), id: 'wt-home', mounted: true };
    // The first tab adopts the scope that existed before tabs (anything selected at startup).
    scopesRef.current = new Map([[first.id, getActiveClientScope()]]);
    setActiveLeaveGuardTab(first.id);
    return { tabs: [first], activeId: first.id };
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  const viewAsRef = useRef(null);
  const limitRef = useRef(onLimitReached);
  limitRef.current = onLimitReached;

  const commit = useCallback((updater) => {
    setState((prev) => {
      const next = updater(prev);
      if (next !== prev) persist(next.tabs, next.activeId, scopesRef.current, viewAsRef.current);
      return next;
    });
  }, []);

  const getScope = useCallback((tabId) => scopesRef.current.get(tabId) || null, []);

  const activate = useCallback((tabId) => {
    const scope = scopesRef.current.get(tabId);
    if (!scope) return;
    // Synchronous so code running right after (and the next render) sees the new tab.
    setActiveClientScope(scope);
    setActiveLeaveGuardTab(tabId);
  }, []);

  /** Patch one tab: `patch` is an object or `(tab) => partialTab`. */
  const updateTab = useCallback((tabId, patch) => {
    commit((prev) => {
      let changed = false;
      const tabs = prev.tabs.map((t) => {
        if (t.id !== tabId) return t;
        const p = typeof patch === 'function' ? patch(t) : patch;
        if (!p) return t;
        const keys = Object.keys(p);
        if (keys.every((k) => Object.is(t[k], p[k]))) return t;
        changed = true;
        return { ...t, ...p };
      });
      return changed ? { ...prev, tabs } : prev;
    });
  }, [commit]);

  /** Id of the tab in front — read at call time, so async callbacks target the current tab. */
  const getActiveTabId = useCallback(() => stateRef.current.activeId, []);

  const switchTab = useCallback((tabId) => {
    if (!scopesRef.current.has(tabId)) return;
    activate(tabId);
    commit((prev) => {
      if (prev.activeId === tabId && prev.tabs.find((t) => t.id === tabId)?.mounted) return prev;
      return {
        activeId: tabId,
        tabs: prev.tabs.map((t) => (t.id === tabId && !t.mounted ? { ...t, mounted: true } : t)),
      };
    });
  }, [activate, commit]);

  /**
   * Open a new tab. `client` (optional) is selected in the new tab's own scope.
   * `background` (Cmd/middle-click, like a browser) leaves the current tab in
   * front; the new tab's page still mounts straight away (hidden) so it is
   * ready when first shown. Returns the new tab id, or null at the tab limit.
   */
  const openTab = useCallback(({ page, section = null, client = null, background = false } = {}) => {
    if (stateRef.current.tabs.length >= MAX_WORKSPACE_TABS) {
      limitRef.current?.();
      return null;
    }
    const tab = makeTab(page || initialPage, section);
    const scope = createClientScope();
    scopesRef.current.set(tab.id, scope);
    if (client?.id) scope.selectClient(client);
    if (!background) activate(tab.id);
    commit((prev) => ({
      activeId: background ? prev.activeId : tab.id,
      tabs: [...prev.tabs, { ...tab, mounted: true }],
    }));
    return tab.id;
  }, [activate, commit, initialPage]);

  const removeTab = useCallback((tabId) => {
    const prev = stateRef.current;
    const idx = prev.tabs.findIndex((t) => t.id === tabId);
    if (idx < 0) return;
    let tabs = prev.tabs.filter((t) => t.id !== tabId);
    let activeId = prev.activeId;
    if (tabs.length === 0) {
      // Never zero tabs: closing the last one leaves a fresh home tab.
      const fresh = { ...makeTab(initialPage), mounted: true };
      scopesRef.current.set(fresh.id, createClientScope());
      tabs = [fresh];
      activeId = fresh.id;
    } else if (activeId === tabId) {
      activeId = tabs[Math.min(idx, tabs.length - 1)].id;
      tabs = tabs.map((t) => (t.id === activeId ? { ...t, mounted: true } : t));
    }
    if (activeId !== prev.activeId) activate(activeId);
    const scope = scopesRef.current.get(tabId);
    scopesRef.current.delete(tabId);
    scope?.dispose();
    commit(() => ({ tabs, activeId }));
  }, [activate, commit, initialPage]);

  /** Close a tab after its unsaved-changes guards agree. Resolves true when closed. */
  const closeTab = useCallback(async (tabId) => {
    if (!(await confirmLeave(tabId))) return false;
    removeTab(tabId);
    return true;
  }, [removeTab]);

  const reorderTabs = useCallback((fromId, toId) => {
    if (fromId === toId) return;
    commit((prev) => {
      const from = prev.tabs.findIndex((t) => t.id === fromId);
      const to = prev.tabs.findIndex((t) => t.id === toId);
      if (from < 0 || to < 0) return prev;
      const tabs = [...prev.tabs];
      const [moved] = tabs.splice(from, 1);
      tabs.splice(to, 0, moved);
      return { ...prev, tabs };
    });
  }, [commit]);

  const cycleTab = useCallback((delta) => {
    const { tabs, activeId } = stateRef.current;
    if (tabs.length < 2) return;
    const idx = tabs.findIndex((t) => t.id === activeId);
    switchTab(tabs[(idx + delta + tabs.length) % tabs.length].id);
  }, [switchTab]);

  /**
   * Replace every tab with the given locations (sign-out, restore after reload).
   * `entries`: [{ page, section, client }]. The first tab's scope is reused.
   */
  const resetTabs = useCallback((entries, activeIndex = 0) => {
    const list = (entries && entries.length ? entries : [{ page: initialPage }]).slice(0, MAX_WORKSPACE_TABS);
    const oldScopes = scopesRef.current;
    const keep = oldScopes.get(stateRef.current.activeId) || createClientScope();
    oldScopes.forEach((scope) => { if (scope !== keep) scope.dispose(); });
    const safeActive = Math.min(Math.max(0, activeIndex), list.length - 1);
    const scopes = new Map();
    const tabs = list.map((entry, i) => {
      const tab = { ...makeTab(entry.page || initialPage, entry.section ?? null), mounted: i === safeActive };
      const scope = i === 0 ? keep : createClientScope();
      scopes.set(tab.id, scope);
      if (entry.client?.id) scope.selectClient(entry.client);
      else if (i === 0) scope.clearSelection();
      return tab;
    });
    scopesRef.current = scopes;
    const activeId = tabs[safeActive].id;
    activate(activeId);
    commit(() => ({ tabs, activeId }));
  }, [activate, commit, initialPage]);

  const setViewAs = useCallback((viewAs) => { viewAsRef.current = viewAs ?? null; }, []);

  /** Save tab locations now (a tab's client changed, which is not tab state). */
  const persistNow = useCallback(() => {
    const { tabs, activeId } = stateRef.current;
    persist(tabs, activeId, scopesRef.current, viewAsRef.current);
  }, []);

  const activeTabRecord = state.tabs.find((t) => t.id === state.activeId) || state.tabs[0];

  return useMemo(() => ({
    tabs: state.tabs,
    activeId: state.activeId,
    activeTabRecord,
    canOpenTab: state.tabs.length < MAX_WORKSPACE_TABS,
    getScope,
    getActiveTabId,
    updateTab,
    switchTab,
    openTab,
    closeTab,
    reorderTabs,
    cycleTab,
    resetTabs,
    setViewAs,
    persistNow,
  }), [state, activeTabRecord, getScope, getActiveTabId, updateTab, switchTab, openTab, closeTab, reorderTabs, cycleTab, resetTabs, setViewAs, persistNow]);
}
